/* SPEC-535 — the sidebar's ONE size scale, big to small as you nest.
 *
 * Param (08 Oct 2026): the group headings (Plan, Buy, Sell, Money, Trust, Admin) read too
 * small, and the sizes should step DOWN as you go deeper: group → page → sub-tab. Before
 * this file each row hard-coded its own size, so the heading (10px capitals) was SMALLER
 * than the page rows (14px) under it — the scale ran the wrong way.
 *
 * Every nav surface reads its size from here and from nowhere else: the group headings and
 * page rows (Sidebar.tsx), the sub-tab strip (SectionNav.tsx), the page-search results and
 * Favourites rows (the same page-row renderer), and the phone menu's foot rows
 * (RailFootRows.tsx). nav-scale.test.ts asserts the steps go down and that no tier is
 * under 12px of readable text.
 *
 * Tailwind reads class names from source text, so every class here is written out in full
 * — never assembled from parts.
 */

export interface NavTier {
  /** Text size in px — the number the step-down test compares. */
  px: number;
  /** The type classes for this tier: size, line height and weight. */
  text: string;
}

export const NAV_SCALE = {
  /** Tier 1 — the group heading (a label and its collapse control, not a destination). */
  group: {
    px: 14,
    text: 'text-[14px] leading-5 font-semibold',
    /** The group's mark, in px (GroupMark `size`). */
    mark: 20,
    /** The collapse chevron. */
    chevron: 'w-4 h-4',
    /** ≥ 40px tall, so the heading stays a comfortable touch target. */
    row: 'min-h-10 px-3 py-2',
  },
  /** Tier 2 — a page row, indented one step from the heading. */
  page: {
    px: 13,
    text: 'text-[13px] leading-5 font-medium',
    /** Resting left pad: one 12px step in from the heading's 12px edge. */
    indent: 'pl-6',
    /** Active row: the 2px left bar takes 2px of the pad, so the text does not move. */
    indentActive: 'pl-[22px]',
    /** ≥ 40px tall on every device (the row is the touch target). */
    row: 'min-h-10 gap-1.5 pr-1 py-2.5',
    /** The "opens further" chevron and the pin star share one icon size. */
    icon: 'w-3.5 h-3.5',
  },
  /** Tier 3 — a sub-tab row, two steps in, under a thin guide line. */
  sub: {
    px: 12,
    /** Size and line height only: the weight is chosen per state (below), never stacked —
     *  two font-weight utilities on one element resolve by stylesheet order. */
    text: 'text-xs leading-4',
    /** Regular at rest; medium marks the tab you are on (with the tint and the bar). */
    weight: 'font-normal',
    weightActive: 'font-medium',
    /** The strip's guide line sits under the page text (one step in); items pad from it. */
    guide: 'ml-6 border-l border-[var(--border-subtle)]',
    /** Comfortable on a mouse; ≥ 40px tall where there is no hover (touch). */
    row: 'py-1.5 [@media(hover:none)]:py-3',
  },
  /** Labels INSIDE the sub-tab strip ("Today", "Forecast"…): small capitals, read as
   *  captions for tier 3. Not a readable-text tier, so not in the step-down order. */
  subLabel: {
    px: 10,
    text: 'text-[10px] leading-4 uppercase tracking-wider font-semibold',
  },
} as const;

/** The readable tiers, outermost first. The step-down test walks this list. */
export const NAV_TIERS: readonly NavTier[] = [NAV_SCALE.group, NAV_SCALE.page, NAV_SCALE.sub];

/** SPEC-535 — does this page open into tabs? True only when at least one tab is left after
 *  the workspace and user filtering (`allowedSubKeys`). `undefined` = a flat page. */
export function opensFurther(visibleKeys: readonly string[] | undefined): boolean {
  return Boolean(visibleKeys && visibleKeys.length > 0);
}

/** SPEC-535 — is a page row's tab list shown?
 *  - `override` is the planner's own chevron click for THIS row (undefined = no click yet).
 *  - `autoOpen` is true for the row that owns the strip of the page on screen.
 *  A click always wins; with no click, the page you are on shows its tabs. A page with no
 *  tabs never expands, whatever the state says. */
export function tabsExpanded(
  hasTabs: boolean,
  override: boolean | undefined,
  autoOpen: boolean,
): boolean {
  if (!hasTabs) return false;
  return override ?? autoOpen;
}
